import { buildAsturHeaders } from '@/endpoints/channel/asturHeaders';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../../requestOptions';

export type CreateCategoryPlanRunBody = Readonly<{
  destination_global_set_id: number;
  destination_subscription_id?: number;
}>;

export type CreateCategoryPlanRunData = Readonly<{
  queued: boolean;
  destination_channel_name: string;
  destination_channel_version: string | null;
  destination_global_set_id: number;
  destination_subscription_id: number | null;
}>;

export async function createCategoryPlanRun(
  scope: RequestScope,
  args: { body: CreateCategoryPlanRunBody },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<CreateCategoryPlanRunData>> {
  try {
    const res = await scopedChannelApi(scope).post<CreateCategoryPlanRunData>('atts/category-plan/runs', args.body, {
      ...options,
      headers: buildAsturHeaders(),
    });
    if (
      typeof res.data?.queued !== 'boolean' ||
      typeof res.data?.destination_channel_name !== 'string' ||
      typeof res.data?.destination_global_set_id !== 'number'
    ) {
      throw new Error('Invalid category plan run response');
    }
    // A well-formed response can legitimately decline to queue (e.g. a run is already in flight for
    // this global set); surface that as a real error response, not a malformed-payload throw.
    if (!res.data.queued) {
      return { isError: true, failureKind: 'failed', status: res.status, errors: { error: 'Category plan run was not queued' } };
    }
    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
